import * as React from "react";
import { cn } from "../lib/utils";

export type AuthSceneLayout = "centered" | "split-left" | "split-right";

export interface AuthSceneProps {
  /** Layout - centered (default) or a 50/50 split (background on the left/right). */
  layout?: AuthSceneLayout;
  /**
   * Content of the decorative area (animated background or photo). Must fill the parent
   * absolutely (`absolute inset-0`) - see `AppBackground`.
   */
  background: React.ReactNode;
  /** Logo (rendered above the form column). */
  logo: React.ReactNode;
  /** In the top-right corner (e.g. a language switcher). */
  topRight?: React.ReactNode;
  /** Card content. */
  children: React.ReactNode;
  className?: string;
}

/**
 * Shared sign-in/sign-up page layout - `web_account` and `web_admin`
 * use the same shell so login/signup/admin look 1:1 the same. Brand colours
 * come from CSS variables (`--color-primary` etc.) → the whole look follows the host theme.
 *
 *  - `centered`     - background full-screen behind the card, logo + card in the center.
 *  - `split-left`   - md+ background panel on the left, form on the right.
 *  - `split-right`  - md+ background panel on the right, form on the left.
 *
 * On mobile, split always falls back to centered (background behind the card).
 */
export function AuthScene({
  layout = "centered",
  background,
  logo,
  topRight,
  children,
  className,
}: AuthSceneProps) {
  const split = layout === "split-left" || layout === "split-right";

  if (!split) {
    return (
      <main
        className={cn(
          "relative isolate flex min-h-dvh items-center justify-center overflow-hidden px-4 py-12",
          className,
        )}
      >
        <div aria-hidden className="fixed inset-0 -z-10 overflow-hidden">
          {background}
        </div>
        {topRight && <div className="absolute right-4 top-4 z-10">{topRight}</div>}
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">{logo}</div>
          {children}
        </div>
      </main>
    );
  }

  // Background on the left → form in the right column, and vice versa.
  const bgLeft = layout === "split-left";

  return (
    <main className={cn("relative isolate min-h-dvh overflow-hidden md:grid md:grid-cols-2", className)}>
      {/* Decorative panel: on mobile full-screen behind the card (fixed, -z-10),
          md+ one half of the grid (relative, so the `absolute inset-0` background fills it). */}
      <div
        aria-hidden
        className={cn(
          "fixed inset-0 -z-10 overflow-hidden md:relative md:inset-auto md:z-auto md:row-start-1",
          bgLeft ? "md:col-start-1" : "md:col-start-2",
        )}
      >
        {background}
      </div>

      {/* Form column */}
      <div
        className={cn(
          "relative z-0 flex min-h-dvh items-center justify-center px-4 py-12 md:row-start-1",
          bgLeft ? "md:col-start-2" : "md:col-start-1",
        )}
      >
        {topRight && <div className="absolute right-4 top-4 z-10">{topRight}</div>}
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center md:justify-start">{logo}</div>
          {children}
        </div>
      </div>
    </main>
  );
}
